/* eslint-disable */
import { Injectable } from '@angular/core';
import { Dates, LocalDate } from './util/dates';
import { DeviceDisabledItem, UiConstants } from '../util/core-utils';
import { MemStorage } from './util/mem-storage';
import { environment } from '../../environments/environment';
import { Order, OrderType, Services } from './util/services';
import { Observable, Observer } from 'rxjs';
import { DeviceManagement } from './device-management.service';
import { RuntimeConfiguration } from './runtime-configuration';
import { LoggedInUserStorage } from './util/storages';
/* eslint-enable */

@Injectable()
export class DeviceSearchService {

  constructor() {
  }

  getSearchData(request: DeviceSearch.SearchDataGetRequest): Observable<DeviceSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<DeviceSearch.SearchDataResult>) => {
      try {
        observer.next(storage.read(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  setSearchData(request: DeviceSearch.SearchDataSetRequest): Observable<DeviceSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<DeviceSearch.SearchDataResult>) => {
      try {
        observer.next(storage.save(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  resetSearchData(request: DeviceSearch.SearchDataGetRequest): Observable<DeviceSearch.SearchDataResult> {
    const storage = this.createStorage();
    return Observable.create((observer: Observer<DeviceSearch.SearchDataResult>) => {
      try {
        observer.next(storage.reset(request));
      }
      catch (e) {
        observer.error(e);
      }
      observer.complete();
      return function () {
      };
    });
  }

  private createStorage(): DeviceSearch.Storage {
    return new DeviceSearch.Storage();
  }

}

export namespace DeviceSearch {

  export interface SearchDataGetRequest {
  }

  export interface SearchDataSetRequest {
    searchData: SearchData;
  }

  export interface SearchDataResult {
    searchData: SearchData;
  }

  export interface SearchData {
    itemsPerPage: number;
    pageNumber: number;
    order: Order<DeviceManagement.OrderField>;
    name: string;
    deviceModel: string;
    latestLoginFrom: LocalDate;
    latestLoginTo: LocalDate;
    latestLoginUser?: number;
    latestLoginUserName: string;
    disabled?: DeviceDisabledItem;
  }

  interface StoredSearchRawDataBase {
    version: number;
  }

  interface StoredSearchRawData extends StoredSearchRawDataBase {
    itemsPerPage: number;
    pageNumber: number;
    order: Order<DeviceManagement.OrderField>;
    name: string;
    deviceModel: string;
    latestLoginFrom?: string;
    latestLoginTo?: string;
    latestLoginUser?: number;
    latestLoginUserName: string;
    disabled?: DeviceDisabledItem;
  }

  export class Storage {

    /**
     * Increment this version if you change the SearchData interface.
     */
    private static readonly VERSION: number = 4;

    private static readonly DEFAULT_SEARCH_DATA: SearchData = {
      itemsPerPage: UiConstants.pageNumbers[0],
      pageNumber: 1,
      order: {
        field: DeviceManagement.OrderField.APPLICATION_ID,
        type: OrderType.DESC
      },
      name: '',
      deviceModel: '',
      latestLoginFrom: Dates.emptyLocalDate(),
      latestLoginTo: Dates.emptyLocalDate(),
      latestLoginUser: undefined,
      latestLoginUserName: '',
      disabled: DeviceDisabledItem.active()
    };

    constructor() {
    }

    public read(request: SearchDataGetRequest): SearchDataResult {
      let rawBase: StoredSearchRawDataBase;
      try {
        const key = this.generateKey();
        const json = MemStorage.getItem(key);
        if (!json) {
          return this.createDefaultResult();
        }
        rawBase = JSON.parse(json);
      }
      catch (error) {
        return this.createDefaultResult();
      }
      if (Storage.VERSION === rawBase.version) {
        const raw: StoredSearchRawData = <StoredSearchRawData> rawBase;
        return this.fromRaw(raw);
      }
      return this.createDefaultResult();
    }

    public reset(request: SearchDataGetRequest): SearchDataResult {
      return this.save({
        searchData: Storage.DEFAULT_SEARCH_DATA,
      });
    }

    public save(request: SearchDataSetRequest): SearchDataResult {
      const key = this.generateKey();
      const raw = this.toRaw(request.searchData);
      const json = JSON.stringify(raw);
      MemStorage.setItem(key, json);
      return this.read(request);
    }

    private generateKey(): string {
      return 'UserId(' + LoggedInUserStorage.getInstance().getUserId() + ')-Search-Device';
    }

    private createDefaultResult(): SearchDataResult {
      return {
        searchData: Storage.DEFAULT_SEARCH_DATA,
      };
    }

    private fromRaw(data: StoredSearchRawData): SearchDataResult {
      return {
        searchData: {
          itemsPerPage: data.itemsPerPage,
          pageNumber: data.pageNumber,
          order: data.order,
          name: data.name,
          deviceModel: data.deviceModel,
          latestLoginFrom: Services.toLocalDate(data.latestLoginFrom),
          latestLoginTo: Services.toLocalDate(data.latestLoginTo),
          latestLoginUser: data.latestLoginUser,
          latestLoginUserName: data.latestLoginUserName,
          disabled: data.disabled
        }
      };
    }

    private toRaw(data: SearchData): StoredSearchRawData {
      return {
        version: Storage.VERSION,
        itemsPerPage: data.itemsPerPage,
        pageNumber: data.pageNumber,
        order: data.order,
        name: data.name,
        deviceModel: data.deviceModel,
        latestLoginFrom: Services.localDateToString(data.latestLoginFrom),
        latestLoginTo: Services.localDateToString(data.latestLoginTo),
        latestLoginUser: data.latestLoginUser,
        latestLoginUserName: data.latestLoginUserName,
        disabled: data.disabled
      };
    }

  }

}
